import { useEffect, useMemo, useState } from "react";
import type { MfgVendorCatalogAlias } from "@/lib/manufacturing/types";
import { listManufacturingVendorSuggestions } from "@/lib/manufacturingClient";
import { ManufacturingVendorAutocomplete } from "@/components/manufacturing/ManufacturingVendorAutocomplete";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  materialId?: string;
  vendor: string;
  primaryAliasId: string;
  onVendorChange: (vendor: string) => void;
  onPrimaryAliasIdChange: (aliasId: string) => void;
};

/**
 * VEN-2b — org vendor typeahead + optional existing-alias picker for the
 * material modal. Save path still lives in the parent form.
 */
export function MaterialModalVendorField({
  materialId,
  vendor,
  primaryAliasId,
  onVendorChange,
  onPrimaryAliasIdChange,
}: Props) {
  const [vendorNames, setVendorNames] = useState<string[]>([]);
  const [catalogAliases, setCatalogAliases] = useState<MfgVendorCatalogAlias[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await listManufacturingVendorSuggestions({ includeAliases: true });
      if (cancelled || !r.ok) return;
      setVendorNames(r.data.vendors.map((v) => v.name));
      setCatalogAliases(Array.isArray(r.data.aliases) ? r.data.aliases : []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const materialAliases = useMemo(() => {
    if (!materialId) return [];
    return catalogAliases.filter((a) => a.material_id === materialId);
  }, [catalogAliases, materialId]);

  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      Vendor
      <ManufacturingVendorAutocomplete
        className="mt-1"
        value={vendor}
        vendors={vendorNames}
        placeholder="Primary vendor (optional)"
        onChange={(next) => {
          onVendorChange(next);
          // Typing a free-text name clears a prior alias pick so save
          // upserts by vendor string (create-or-match).
          if (primaryAliasId) onPrimaryAliasIdChange("");
        }}
      />
      {materialAliases.length > 0 ? (
        <select
          className={`${inputClass} mt-2`}
          value={primaryAliasId}
          onChange={(e) => {
            const id = e.target.value;
            onPrimaryAliasIdChange(id);
            const hit = materialAliases.find((a) => a.id === id);
            if (hit) onVendorChange(hit.vendor_name);
          }}
          aria-label="Existing vendor alias for this material"
        >
          <option value="">Use vendor name above (create / match)</option>
          {materialAliases.map((a) => (
            <option key={a.id} value={a.id}>
              {a.vendor_name}
              {a.vendor_sku ? ` · ${a.vendor_sku}` : ""}
            </option>
          ))}
        </select>
      ) : null}
      <span className="mt-1 block text-[11px] text-gray-500 dark:text-zinc-400">
        Typeahead from this org&apos;s vendor catalog. Save upserts an alias and stamps the
        primary vendor pointer.
      </span>
    </label>
  );
}
